import {is} from 'immutable';
// @ts-expect-error - TS7016 - Could not find a declaration file for module 'rafu'.
import {frame} from 'rafu';
import {recordEffect} from './FluxPerformanceUtil';
import {ChangeCallback, ChangeListener} from './types';
import {
  type FluxMetricsContext,
  incrementFluxMetric,
} from '@packages/systems/designer-telemetry';

import {SyncLane, NoLane, type Lane} from './Lanes';

let rAF = frame;

if (typeof document !== 'undefined') {
  const swapRAF = () => {
    // Use different approach when tab is hidden since rAF is only called when the designer is visible (only in Chrome). This
    // fix prevents issues where engines don't receive updates from stores (particularly engines that need to receive requests via websockets).
    if (document.hidden) {
      rAF = (tick: () => void) => setTimeout(tick, 0);
    } else {
      // continue to use rAF to prevent layout thrashing
      rAF = frame;
    }
  };
  document.addEventListener('visibilitychange', swapRAF);
  swapRAF();
}

export class Store<State extends Record<string, any>> {
  displayName: string;
  state: State;
  private committedState: Map<ChangeCallback<State>, State>;
  private changeCallbacks: {
    [K in Lane]: Set<ChangeCallback<State>>;
  };
  private scheduledNoLaneFlush: boolean = false;
  private pendingFlushCallbacks: Array<() => void> = [];
  private getMetricsContext: (() => FluxMetricsContext | undefined) | undefined;

  constructor(
    name: string,
    initialState: State,
    getMetricsContext?: () => FluxMetricsContext | undefined
  ) {
    this.displayName = name;
    this.state = initialState;
    this.committedState = new Map();
    this.changeCallbacks = {
      [SyncLane]: new Set(),
      [NoLane]: new Set(),
    };
    this.getMetricsContext = getMetricsContext;
  }

  onChange: ChangeListener<State> = (fn) => {
    return this.addListener(fn, NoLane);
  };

  onChangeSync: ChangeListener<State> = (fn) => {
    return this.addListener(fn, SyncLane);
  };

  addListener(changeCallback: ChangeCallback<State>, lane: Lane) {
    this.changeCallbacks[lane].add(changeCallback);
    /**
     * Unfortuantely code in BridgeHelper and likely other places
     * actually expect to be called once with the prevState being
     * undefined even though there is a valid initialState.
     *
     * So conceptually we should have this code, in practice, we
     * still give one invocation of undefined as the previous state.
     */
    // this.committedState.set(changeCallback, this.state);

    return () => {
      this.changeCallbacks[lane].delete(changeCallback);
      this.committedState.delete(changeCallback);
    };
  }

  emitChange = (lane: Lane = NoLane) => {
    this.flushLane(SyncLane);

    if (lane === SyncLane) {
      this.flushNoLane();
    } else if (lane === NoLane) {
      if (!this.scheduledNoLaneFlush) {
        rAF(this.flushNoLane);
        this.scheduledNoLaneFlush = true;
      }
    }
  };

  /**
   * Registers a callback to be invoked after the next NoLane flush completes.
   * If no flush is currently scheduled, the callback is invoked immediately.
   * This is used by Flux to defer ending metrics collection until after
   * async subscribers have been notified.
   */
  onNextFlush = (callback: () => void) => {
    if (this.scheduledNoLaneFlush) {
      this.pendingFlushCallbacks.push(callback);
    } else {
      // No flush scheduled, invoke immediately
      callback();
    }
  };

  private flushLane = (lane: Lane) => {
    this.changeCallbacks[lane].forEach(this.invokeChangeCallback);
  };

  private invokeChangeCallback = (changeCallback: ChangeCallback<State>) => {
    recordEffect();
    const previousState = this.committedState.get(changeCallback);
    const state = this.state;

    if (this.displayName === '@webflow/FluxRoot' || !is(state, previousState)) {
      changeCallback(state, previousState);
      this.committedState.set(changeCallback, state);

      // Track subscriber metrics (incrementFluxMetric is a no-op if ctx is undefined)
      const ctx = this.getMetricsContext?.();
      incrementFluxMetric(ctx, 'subscriberEffectCount');
      if (this.displayName === '@webflow/FluxRoot') {
        incrementFluxMetric(ctx, 'rootSubscriptionsInvokedCount');
      }
    }
  };

  private flushNoLane = () => {
    this.scheduledNoLaneFlush = false;
    this.flushLane(NoLane);

    // Invoke any pending flush callbacks
    const callbacks = this.pendingFlushCallbacks;
    this.pendingFlushCallbacks = [];
    for (const callback of callbacks) {
      callback();
    }
  };
}
